import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { stockService } from '../services/api';
import { DotLoader } from 'react-spinners';
import './BuySignals.css';

const BuySignals = ({ sectors, stockData, min22Data }) => {
  const [maData, setMaData] = useState({});
  const [favorites, setFavorites] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch MA data and watchlist on mount
  useEffect(() => {
    const controller = new AbortController();
    stockService.getMAOverview(controller.signal)
      .then(res => {
        if (res && res.stocks) {
          const map = {};
          res.stocks.forEach(s => { map[s.symbol] = s; });
          setMaData(map);
        }
        setLoading(false);
      })
      .catch(err => {
        if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
          console.error('Error fetching MA:', err);
          setLoading(false);
        }
      });

    stockService.getWatchlist()
      .then(res => {
        if (res && res.watchlist) {
          setFavorites(new Set(res.watchlist.map(w => w.symbol)));
        }
      })
      .catch(err => console.error('Error fetching watchlist:', err));

    return () => controller.abort();
  }, []);

  // Toggle favorite
  const toggleFavorite = useCallback(async (symbol) => {
    const isFav = favorites.has(symbol);
    setFavorites(prev => {
      const next = new Set(prev);
      if (isFav) next.delete(symbol);
      else next.add(symbol);
      return next;
    });
    try {
      if (isFav) {
        await stockService.removeFromWatchlist(symbol);
      } else {
        await stockService.addToWatchlist(symbol);
      }
    } catch (err) {
      setFavorites(prev => {
        const next = new Set(prev);
        if (isFav) next.add(symbol);
        else next.delete(symbol);
        return next;
      });
      console.error('Error toggling favorite:', err);
    }
  }, [favorites]);

  // Compute sector breadth
  const sectorBreadth = useMemo(() => {
    const breadth = {};
    sectors.forEach(sector => {
      let above = 0, total = 0;
      sector.symbols.forEach(sym => {
        const ma = maData[sym];
        if (ma && ma.ma20_distance != null) {
          total++;
          if (ma.ma20_distance > 0) above++;
        }
      });
      const pct = total > 0 ? (above / total) * 100 : 0;
      sector.symbols.forEach(sym => {
        breadth[sym] = { sectorName: sector.name, breadthMA20: Math.round(pct) };
      });
    });
    return breadth;
  }, [sectors, maData]);

  // Score and filter stocks
  const signals = useMemo(() => {
    if (!Object.keys(stockData).length || !Object.keys(maData).length) return [];

    const allSymbols = sectors.flatMap(s => s.symbols);
    const scored = [];

    allSymbols.forEach(symbol => {
      const stock = stockData[symbol];
      const ma = maData[symbol];
      const min22 = min22Data[symbol];
      const breadth = sectorBreadth[symbol];
      if (!stock || !ma) return;

      const price = stock.price;
      const changePct = stock.change_percent || 0;
      const volumeRatio = stock.percentVolumeMA20 || 0;
      const ma20Dist = ma.ma20_distance;
      const ma50Dist = ma.ma50_distance;
      const ma200Dist = ma.ma200_distance;
      const min22Pct = min22?.percent;
      const sectorBreath = breadth?.breadthMA20 || 0;
      // Filters — "đầu chu kì tăng"
      // MA20: between -5% and +8% (near or just crossed)
      if (ma20Dist == null || ma20Dist < -5 || ma20Dist > 8) return;
      // Min22: ≤ 15% (hasn't run too far)
      if (min22Pct != null && min22Pct > 15) return;
      // Today must be green or flat
      if (changePct < -0.5) return;

      // === SCORING ===
      let score = 0;

      // 1. MA20 crossover (max 30pts) — best when 0 to +3%
      if (ma20Dist >= 0 && ma20Dist <= 3) score += 30;
      else if (ma20Dist > 3 && ma20Dist <= 5) score += 20;
      else if (ma20Dist > 5) score += 10;
      else if (ma20Dist >= -3 && ma20Dist < 0) score += 15; // about to cross
      else score += 5;

      // 2. Near 22-session low (max 20pts) — closer = better opportunity
      if (min22Pct != null) {
        if (min22Pct <= 3) score += 20;
        else if (min22Pct <= 5) score += 15;
        else if (min22Pct <= 8) score += 10;
        else score += 5;
      }

      // 3. Volume ratio (max 20pts)
      if (volumeRatio >= 1.5) score += 20;
      else if (volumeRatio >= 1.0) score += 15;
      else if (volumeRatio >= 0.8) score += 10;
      else score += 3;

      // 4. Today's change (max 15pts)
      if (changePct >= 3) score += 15;
      else if (changePct >= 1) score += 12;
      else if (changePct >= 0) score += 8;
      else score += 0;

      // 5. Sector breadth (max 15pts)
      if (sectorBreath >= 60) score += 15;
      else if (sectorBreath >= 40) score += 10;
      else if (sectorBreath >= 25) score += 5;
      else score += 0;

      // Bonus: MA50 also positive
      if (ma50Dist != null && ma50Dist > 0) score += 5;
      // Bonus: MA200 positive (long-term uptrend)
      if (ma200Dist != null && ma200Dist > 0) score += 5;

      scored.push({
        symbol,
        price,
        changePct,
        volumeRatio,
        ma20Dist,
        ma50Dist,
        ma200Dist,
        min22Pct,
        sectorName: breadth?.sectorName || '',
        sectorBreath,
        score,
      });
    });

    // Sort by score descending
    scored.sort((a, b) => b.score - a.score);
    return scored;
  }, [stockData, maData, min22Data, sectorBreadth, sectors]);

  const getScoreClass = (score) => {
    if (score >= 80) return 'score-excellent';
    if (score >= 60) return 'score-good';
    if (score >= 40) return 'score-fair';
    return 'score-weak';
  };

  const getChangeClass = (pct) => {
    if (pct > 0) return 'text-green';
    if (pct < 0) return 'text-red';
    return 'text-neutral';
  };

  const getMAClass = (dist) => {
    if (dist == null) return 'text-neutral';
    if (dist > 0) return 'text-green';
    return 'text-red';
  };

  const isLoading = loading || sectors.length === 0;

  return (
    <div className="signals-container">
      {isLoading && (
        <div className="signals-loading">
          <DotLoader color="#3b82f6" size={50} />
        </div>
      )}

      <div className="signals-header">
        <h2>Tín hiệu mua</h2>
        <span className="signals-count">{signals.length} mã phù hợp</span>
      </div>

      <div className="signals-legend">
        <span>Tiêu chí: MA20 (-5% → +8%) · Gần đáy 22 phiên (≤15%) · Hôm nay không giảm quá 0.5%</span>
      </div>

      {error && (
        <div className="signals-error">{error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="signals-table-wrapper">
        <table className="signals-table">
          <thead>
            <tr>
              <th className="col-fav"></th>
              <th className="col-rank">#</th>
              <th className="col-sym">Mã</th>
              <th className="col-sector">Ngành</th>
              <th className="col-price">Giá</th>
              <th className="col-chg">+/- %</th>
              <th className="col-vol">KL/MA20</th>
              <th className="col-ma">MA20</th>
              <th className="col-ma">MA50</th>
              <th className="col-ma">MA200</th>
              <th className="col-min22">Min22 %</th>
              <th className="col-breadth">Ngành %</th>
              <th className="col-score">Điểm</th>
            </tr>
          </thead>
          <tbody>
            {signals.map((s, i) => (
              <tr key={s.symbol}>
                <td className="col-fav">
                  <button className={`fav-btn${favorites.has(s.symbol) ? ' active' : ''}`} onClick={() => toggleFavorite(s.symbol)}>
                    ★
                  </button>
                </td>
                <td className="col-rank">{i + 1}</td>
                <td className="col-sym">{s.symbol}</td>
                <td className="col-sector">{s.sectorName}</td>
                <td className="col-price">{s.price}</td>
                <td className={`col-chg ${getChangeClass(s.changePct)}`}>
                  {s.changePct > 0 ? '+' : ''}{s.changePct?.toFixed(2)}%
                </td>
                <td className="col-vol">{s.volumeRatio?.toFixed(2)}</td>
                <td className={`col-ma ${getMAClass(s.ma20Dist)}`}>
                  {s.ma20Dist != null ? `${s.ma20Dist > 0 ? '+' : ''}${s.ma20Dist.toFixed(1)}` : '-'}
                </td>
                <td className={`col-ma ${getMAClass(s.ma50Dist)}`}>
                  {s.ma50Dist != null ? `${s.ma50Dist > 0 ? '+' : ''}${s.ma50Dist.toFixed(1)}` : '-'}
                </td>
                <td className={`col-ma ${getMAClass(s.ma200Dist)}`}>
                  {s.ma200Dist != null ? `${s.ma200Dist > 0 ? '+' : ''}${s.ma200Dist.toFixed(1)}` : '-'}
                </td>
                <td className="col-min22">
                  {s.min22Pct != null ? `${s.min22Pct.toFixed(1)}%` : '-'}
                </td>
                <td className="col-breadth">{s.sectorBreath}%</td>
                <td className={`col-score ${getScoreClass(s.score)}`}>{s.score}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BuySignals;
